// Task #2161 — shared "Copy as is" / "Copy & reframe" helpers used by the
// My Ads library and both Ads Express grids.
//
// - duplicateAdAsIs: POSTs the server duplicate endpoint, which clones the
//   saved ad into a new parent generation + row with its OWN R2 object
//   (fully independent from the original — edits/deletes never cross).
// - copyAndReframeAd: runs the EXISTING reframe pipeline against the source
//   ad, then saves the result as a new library ad via the standard
//   save-generated-image flow (which mirrors it into its own R2 object).
//   The original ad is never touched.
import { apiRequest } from "@/lib/queryClient";
import type { AspectRatio } from "@shared/aspectRatios";

export async function duplicateAdAsIs(adId: string): Promise<{ generatedAdId: string; ad: unknown }> {
  const res = await apiRequest("POST", `/api/generated-ads/${adId}/duplicate`, {});
  return res.json();
}

type ReframeSource = {
  brandId: string;
  adUnitId: string | null;
  personaId: string | null;
  productId: string | null;
  narrativeAngleId: string | null;
  projectId: string | null;
  parentAdId: string;
  prompt: string | null;
  // Present only on the safe_zone_reframe granted arm — passed through so
  // the persisted copy is normalized to exact dimensions + ratio-stamped.
  aspectRatio?: string | null;
};

export async function copyAndReframeAd(
  adId: string,
  aspectRatio: AspectRatio,
): Promise<{ generatedAdId: string; ad: unknown }> {
  const reframeRes = await apiRequest("POST", `/api/generated-ads/${adId}/reframe`, { aspectRatio });
  const data = (await reframeRes.json()) as {
    imageUrl: string;
    aspectRatio: string;
    source: ReframeSource;
  };
  if (!data?.imageUrl) throw new Error("No image returned from reframe");
  if (!data.source?.adUnitId) {
    console.error("[reframe] copy save blocked: response is missing its ad-unit reference", {
      adId,
      brandId: data.source?.brandId ?? null,
      parentAdId: data.source?.parentAdId ?? null,
    });
    throw new Error("We couldn't save this version. Please try again, or contact support if it keeps happening.");
  }
  const saveRes = await apiRequest(
    "POST",
    `/api/brands/${data.source.brandId}/library/save-generated-image`,
    {
      imageUrl: data.imageUrl,
      parentAdId: adId,
      adUnitId: data.source.adUnitId,
      personaId: data.source.personaId,
      productId: data.source.productId,
      narrativeAngleId: data.source.narrativeAngleId,
      projectId: data.source.projectId,
      prompt: data.source.prompt,
      aspectRatio,
    },
  );
  return saveRes.json();
}
